<template>
	<header-block title="Add more details" bgcolor="white" border="0"></header-block>
	<CustomerLoadWait :customer="customer && policy"/>

	<transition>
		<div v-if="customer && policy" class="more-details-page">
			<!-- Policy Card -->
			<div class="policy-card-container">
				<AccountListItem
					:provider-name="policy.provider?.name || 'Provider'"
					:line1="policy.policyType?.name || 'Insurance'"
					:line2="policy.policyNumber || ''"
					:value="formatDate(policy.endDate)"
					label="Renewal"
					:avatar-url="policy.provider?.logoUri || require('@/assets/round1.png')"
				/>
			</div>

			<!-- Title and Subtitle -->
			<div class="content-section">
				<h1 class="page-title">Add more details (optional)</h1>
				<p class="page-subtitle">If you have them to hand, these details help keep everything in one place.</p>

				<!-- WhatsApp Promo Card -->
				<WhatsAppPromoCard
					ref="whatsAppPromoCard"
					v-if="showWhatsappCard"
					subtitle="We'll add the key details and save the document securely. You can also chat with our WhatsApp assistant about this policy."
					@close="showWhatsappCard = false"
					@action="handleWhatsAppAction"
				class="whatsapp-promo"
				/>

				<h1 class="page-title">Policy details</h1>
				<!-- <p class="page-subtitle">Lorem ipsum dolor sit amet consectetur.</p> -->
				<!-- Policy Details Form -->
				<InputSections
					v-if="sections.length"
					ref="inputSections"
					:sections="sections"
					:formatter="formatter"
					theme="light"
					:editMode="true"
					:outline="false"
				/>
			</div>

			<h1 class="page-title">About the cover</h1>
			<!-- <p class="page-subtitle">Lorem ipsum dolor sit amet consectetur.</p> -->

			<InputSections
					v-if="policySections.length"
					ref="policySectionsInput"
					:sections="policySections"
					:formatter="formatter"
					theme="light"
					:editMode="true"
					:outline="false"
				/>

			<h1 class="page-title">Cover & limits</h1>
			<p class="page-subtitle">What's covered and where the limits apply.</p>

			<InputSections
					v-if="coverSections.length"
					ref="coverSectionsInput"
					:sections="coverSections"
					:formatter="formatter"
					theme="light"
					:editMode="true"
					:outline="false"
				/>

			<!-- Getting Help Section -->
			<!-- <div class="help-card">
				<h1 class="help-card-title">Getting help</h1>
				<p class="help-card-subtitle">Useful contact details if you ever need to make a claim or ask a question.</p>

				<InputSections
					v-if="helpSections.length"
					ref="helpSectionsInput"
					:sections="helpSections"
					:formatter="formatter"
					theme="light"
					:editMode="true"
					:outline="false"
				/>
			</div> -->

			<!-- Save Button -->
			<div class="anchor-bottom-no-nav">
				<MGButton text="Save changes" :fullWidth="true" @click="saveChanges"/>
			</div>
		</div>
	</transition>
</template>

<script>
import Services from '../mixins/Services';
import Helpers from '../mixins/Helpers';
import CustomerLoadWait from './elements/CustomerLoadWait.vue';
import HeaderBlock from './elements/HeaderBlock.vue';
import InputSections from './elements/InputSections.vue';
import MGButton from './elements/MGButton.vue';
import AccountListItem from './elements/AccountListItem.vue';
import WhatsAppPromoCard from './elements/WhatsAppPromoCard.vue';

export default {
	mixins: [Services, Helpers],
	components: { HeaderBlock, InputSections, MGButton, CustomerLoadWait, AccountListItem, WhatsAppPromoCard },
	data() {
		return {
			sections: [],
			policySections: [],
			coverSections: [],
			helpSections: [],
			showWhatsappCard: true
		}
	},
	computed: {
		policyId() {
			return this.$route.query.policyId;
		}
	},
	methods: {
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			const options = { day: 'numeric', month: 'long', year: 'numeric' };
			return date.toLocaleDateString('en-GB', options);
		},
		populateFields(sections) {
			if (!this.policy) return sections;

			// Pre-fill read-only fields
			const providerField = this._findFieldByIdInSections(sections, 'provider');
			if (providerField) {
				providerField.value = this.policy.provider?.name || '';
			}

			const renewalDateField = this._findFieldByIdInSections(sections, 'renewalDate');
			if (renewalDateField) {
				renewalDateField.value = this.formatDate(this.policy.endDate);
			}

			// Pre-fill editable fields if they exist
			const payFrequencyField = this._findFieldByIdInSections(sections, 'payFrequency');
			if (payFrequencyField && this.policy.payFrequency) {
				payFrequencyField.value = this.policy.payFrequency;
			} else if (payFrequencyField) {
				// Infer from premium data
				if (this.policy.annualPremium) {
					payFrequencyField.value = 'Annually';
				} else if (this.policy.monthlyPremium) {
					payFrequencyField.value = 'Monthly';
				}
			}

			const policyNumberField = this._findFieldByIdInSections(sections, 'policyNumber');
			if (policyNumberField && this.policy.policyNumber) {
				policyNumberField.value = this.policy.policyNumber;
			}

			const premiumField = this._findFieldByIdInSections(sections, 'premium');
			if (premiumField) {
				premiumField.value = this.policy.annualPremium || this.policy.monthlyPremium || null;
			}

			return sections;
		},
		findFieldValueInAllSections(fieldId) {
			const allSections = [
				...this.sections,
				...this.policySections,
				...this.coverSections,
				...this.helpSections
			];
			return this._findFieldValueByIdInSections(allSections, fieldId);
		},
		saveChanges() {
			const payFrequency = this.findFieldValueInAllSections('payFrequency');
			const policyNumber = this.findFieldValueInAllSections('policyNumber');
			const premium = this.findFieldValueInAllSections('premium');
			const excess = this.findFieldValueInAllSections('excess');
			const voluntaryExcess = this.findFieldValueInAllSections('voluntaryExcess');
			const totalExcess = (parseFloat(excess) || 0) + (parseFloat(voluntaryExcess) || 0);
			const sumInsured = this.findFieldValueInAllSections('sumInsured');

			// Handle address update
			const addressObject = this.findFieldValueInAllSections('address');
			let insuredItems = this.policy.insuredItems;
			if (addressObject && insuredItems && insuredItems.length > 0) {
				insuredItems[0].addressInsured = {
					line1: addressObject?.line1 || null,
					line2: addressObject?.line2 || null,
					line3: addressObject?.line3 || null,
					town: addressObject?.town || null,
					postCode: addressObject?.postCode || null,
				};
			}

			const updatedPolicy = {
				id: this.policyId,
				policyType: this.policy.policyType,
				provider: this.policy.provider,
				insuredItems: insuredItems,
				startDate: this.policy.startDate,
				endDate: this.policy.endDate,
				payFrequency: payFrequency || null,
				policyNumber: policyNumber || null,
				annualPremium: payFrequency === 'Annually' ? premium : null,
				monthlyPremium: payFrequency === 'Monthly' ? premium : null,
				excess: totalExcess || this.policy.excess,
				voluntaryExcess: voluntaryExcess || this.policy.voluntaryExcess,
				noClaimsYears: this.policy.noClaimsYears,
				soleOrJoint: this.policy.soleOrJoint,
				sumAssured: sumInsured || this.policy.sumAssured,
				coverType: this.policy.coverType,
				travelCoverType: this.policy.travelCoverType,
				travelCoverage: this.policy.travelCoverage
			};

			this._updatePolicy(updatedPolicy, () => {
				this._getPolicy(this.policyId, null, true);
				this._getInsuranceInfo(null, true);
				this.$router.push('/money');
			});
		},
		handleWhatsAppAction() {
			if (this.customer) {
				if (this.customer.person.whatsAppEnabled) {
					var request = {
						"body": "You indicated you wanted to send a document 📄  Just drop it here and we will upload and process it for you 👍",
						"whatsApp": true,
						"whatsAppTemplate": false,
						"phoneNumber": this.customer.person.mobilePhone
					};
					this.$http.post(this.api + "/Customers/00000000-0000-0000-0000-000000000000/Notifications", request);
					if (this.$refs.whatsAppPromoCard) this.$refs.whatsAppPromoCard.setClicked();
				} else {
					this.$router.push('/#/enablewhatsapp');
				}
			}
		},
		onLoad: async function() {
			this._getAiopSection('InsuranceAdditionalSection.json', () => {
				this.sections = this.sectionJson;
				this._getCustomer();
				this._getInsuranceProviders((res) => {
					res = res.filter(item => item.buildings === true || item.contents === true || item.gadget === true
							|| item.car === true || item.motorbike === true || item.pet === true
							|| item.life === true || item.other === true)
							.filter((item, index, self) =>
							index === self.findIndex((t) => t.name === item.name)
						);
					var providerField = this._findFieldById('provider');
					if (providerField) {
						providerField.items = res.map(provider => ({
							name: provider.name.replace(/\w\S*/g, (txt) => {
								return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
							}),
							logoUri: provider.logoUri
						}));
					}
				});

				// Load the "About the cover" section
				this._getAiopSection('InsuranceAdditionalAbout.json', () => {
					const aboutSections = this.sectionJson;

					// Load the "Cover & limits" section
					this._getAiopSection('InsuranceAdditionalCover.json', () => {
						const coverSectionsJson = this.sectionJson;

						// Load the "Getting help" section
						this._getAiopSection('InsuranceAdditionalGettingHelp.json', () => {
							const helpSectionsJson = this.sectionJson;

							if (this.policyId) {
								this._getPolicy(this.policyId, (result) => {
									this.policy = result;
									const policyType = this.policy.policyType?.name;

									this.sections = this.populateFields(this.sections);

									// Filter sections by policy type and populate
									this.policySections = this._filterSectionsByRule(aboutSections, 'policyType', policyType);
									this.policySections = this.populateFields(this.policySections);

									this.coverSections = this._filterSectionsByRule(coverSectionsJson, 'policyType', policyType);
									this.coverSections = this.populateFields(this.coverSections);

									this.helpSections = this._filterSectionsByRule(helpSectionsJson, 'policyType', policyType);
									this.helpSections = this.populateFields(this.helpSections);
								});
							} else {
								this.$router.push('/money');
							}
						});
					});
				});
			});
		}
	},
	mounted: function () {
		this.onLoad();
	}
}
</script>

<style scoped>
.more-details-page {
	padding-bottom: 100px;
}

.policy-card-container {
	margin-bottom: 24px;
	margin-left: -30px;
	margin-right: -30px;
}

/* Hide arrow on the policy card */
.policy-card-container :deep(.arrow-icon) {
	display: none;
}

/* Disable hover effect on policy card */
.policy-card-container :deep(.account-list-item) {
	cursor: default;
}

.policy-card-container :deep(.account-list-item:hover) {
	background-color: white;
}

.content-section {
	padding: 0 4px;
}

.page-title {
	font-size: 18px;
	font-weight: bold;
	margin-bottom: 8px;
	color: var(--text-color);
}

.page-subtitle {
	font-size: 14px;
	line-height: 1.4;
	color: var(--text-color);
	margin-bottom: 24px;
}

.help-card {
	background: white;
	border-radius: 16px;
	padding: 16px;
	margin-top: 24px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.help-card-title {
	font-size: 15px;
	font-weight: bold;
	margin: 0;
}

.help-card-subtitle {
	font-size: 13px;
	line-height: 1.23;
	margin: -16px 0 0 0;
}

.help-card :deep(.v-input) {
	margin-bottom: -12px;
}

.whatsapp-promo {
	margin-bottom: 24px;
}
</style>
